/**
 * Next-track head prefetch (spec.md §10.8).
 *
 * About 20 seconds before the current track ends, the first 512 KB of the *next* track are
 * fetched into the HTTP cache. When the queue advances, `<audio>` asks for the same bytes,
 * the browser answers from cache, and the gap between tracks disappears.
 *
 * ## The three things that make this work at all
 *
 * 1. **`Range: bytes=0-524287`, exactly one range.** `Range` is a CORS-safelisted request
 *    header **only** for a single range with a non-null first-byte-pos, so this form
 *    triggers no preflight. A second range (`bytes=0-1,10-20`) would, and the OPTIONS
 *    round-trip would cost more than the prefetch saves.
 * 2. **The URL must be byte-identical to the one `<audio>` will request.** That is why the
 *    media token is minted per batch with a fixed `exp` rather than per request: a
 *    differing query string is a different cache key and the prefetch is pure waste. Never
 *    "helpfully" append a cache-buster here.
 * 3. **The response must be cacheable** — `Cache-Control: private, max-age=604800,
 *    immutable` plus a strong `ETag`, which the API's range handler sets. Without them the
 *    browser re-requests and the prefetch is, again, pure waste.
 *
 * ## Why it is skipped on a slow or metered connection
 *
 * 512 KB of speculative data is a real cost on a metered plan, and on 2G the prefetch
 * competes with the audio actually playing — it would cause the stall it exists to prevent.
 * `navigator.connection.saveData` and `effectiveType` are both honoured, and an absent
 * `connection` (every iOS browser) means "proceed", because unknown must not mean blocked.
 *
 * ## Best-effort means best-effort
 *
 * Nothing here ever rejects or throws. A failed prefetch is invisible to the user: the
 * track still plays, just without the head start. The result value says what happened for
 * logging, and that is the whole error contract.
 */

/** The head of the file to warm, in bytes. Matches the API's first aligned chunk. */
export const PREFETCH_HEAD_BYTES = 524_288;

/** How long before the end of the current track the prefetch is issued (spec.md §10.8). */
export const PREFETCH_LEAD_MS = 20_000;

/**
 * `effectiveType` values on which the prefetch is skipped.
 *
 * `3g` is deliberately **not** here: it is slow, but 512 KB over ~20 s is affordable and
 * it is the connection class that benefits most from the head start.
 */
export const SLOW_EFFECTIVE_TYPES: readonly string[] = ['slow-2g', '2g'];

/** The `NetworkInformation` fields we read. Absent on every iOS browser. */
export interface NetworkInformationLike {
  readonly saveData?: boolean;
  readonly effectiveType?: string;
}

export type PrefetchSkipReason =
  /** `navigator.connection.saveData` is on — the user asked us not to. */
  | 'save-data'
  /** `effectiveType` is 2g or slower. */
  | 'slow-network'
  /** No `fetch` in this environment. */
  | 'no-fetch'
  /** Empty or non-string URL. */
  | 'no-url';

export type PrefetchResult =
  | { readonly status: 'ok'; readonly bytes: number; readonly httpStatus: number }
  | { readonly status: 'skipped'; readonly reason: PrefetchSkipReason }
  | { readonly status: 'aborted' }
  | { readonly status: 'failed'; readonly error: unknown };

/** Reads `navigator.connection` (and its two vendor-prefixed spellings) defensively. */
export function resolveConnection(): NetworkInformationLike | null {
  if (typeof navigator === 'undefined') return null;
  try {
    const nav = navigator as unknown as {
      connection?: unknown;
      mozConnection?: unknown;
      webkitConnection?: unknown;
    };
    const candidate = nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
    if (typeof candidate !== 'object' || candidate === null) return null;
    return candidate;
  } catch {
    return null;
  }
}

/**
 * Whether a prefetch may be issued on this connection.
 *
 * `undefined`/`null` — the API is missing — returns `true`: an unknown connection is not
 * evidence of a bad one.
 */
export function shouldPrefetch(connection?: NetworkInformationLike | null): boolean {
  if (connection === undefined || connection === null) return true;
  if (connection.saveData === true) return false;
  const effectiveType = connection.effectiveType;
  if (typeof effectiveType === 'string' && SLOW_EFFECTIVE_TYPES.includes(effectiveType)) {
    return false;
  }
  return true;
}

/**
 * The single-range header value: `bytes=0-<n-1>`.
 *
 * Inclusive end, per RFC 7233 — `bytes=0-524287` is 524288 bytes, and the off-by-one is
 * the classic way to make the range not line up with the server's chunk alignment.
 */
export function rangeHeaderValue(bytes: number = PREFETCH_HEAD_BYTES): string {
  const size = Number.isFinite(bytes) && bytes > 0 ? Math.floor(bytes) : PREFETCH_HEAD_BYTES;
  return `bytes=0-${String(size - 1)}`;
}

/** `true` when the current track is close enough to its end to warm the next one. */
export function isWithinPrefetchWindow(remainingS: number, leadMs = PREFETCH_LEAD_MS): boolean {
  if (!Number.isFinite(remainingS) || remainingS < 0) return false;
  return remainingS * 1000 <= leadMs;
}

export interface PrefetchOptions {
  /** How many bytes of the head to warm. Defaults to {@link PREFETCH_HEAD_BYTES}. */
  readonly bytes?: number;
  /** Caller's abort signal — aborting it aborts the request. */
  readonly signal?: AbortSignal;
  /** Injectable `fetch`, for tests. */
  readonly fetchImpl?: typeof fetch;
  /**
   * Injectable connection info. Pass `null` to force the "unknown connection" path;
   * omit to read `navigator.connection`.
   */
  readonly connection?: NetworkInformationLike | null;
}

/** A running prefetch. `promise` never rejects. */
export interface PrefetchHandle {
  readonly promise: Promise<PrefetchResult>;
  /** Cancels the request. Safe after completion, safe to call twice. */
  abort(): void;
}

function isAbort(error: unknown): boolean {
  if (error instanceof Error) return error.name === 'AbortError';
  if (typeof error === 'object' && error !== null && 'name' in error) {
    return error.name === 'AbortError';
  }
  return false;
}

/**
 * Warms the head of `url`.
 *
 * The request is issued with `priority: 'low'` so it queues behind the audio element's own
 * range requests — a prefetch that delays the track currently playing is worse than no
 * prefetch. The body is drained rather than discarded, because the HTTP cache entry is
 * only guaranteed to be written once the response has been read to completion; the 512 KB
 * buffer is then dropped on the floor, which is the point.
 */
export function prefetchHead(url: string, options: PrefetchOptions = {}): PrefetchHandle {
  const controller = new AbortController();
  const abort = (): void => {
    try {
      controller.abort();
    } catch {
      // Already aborted, or a webview with a half-implemented AbortController.
    }
  };

  const skip = (reason: PrefetchSkipReason): PrefetchHandle => ({
    promise: Promise.resolve<PrefetchResult>({ status: 'skipped', reason }),
    abort: () => {},
  });

  if (typeof url !== 'string' || url === '') return skip('no-url');

  const connection = options.connection === undefined ? resolveConnection() : options.connection;
  if (!shouldPrefetch(connection)) {
    return skip(connection?.saveData === true ? 'save-data' : 'slow-network');
  }

  const doFetch = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : undefined);
  if (doFetch === undefined) return skip('no-fetch');

  const external = options.signal;
  if (external !== undefined) {
    if (external.aborted) abort();
    else external.addEventListener('abort', abort, { once: true });
  }

  const run = async (): Promise<PrefetchResult> => {
    try {
      const response = await doFetch(url, {
        method: 'GET',
        // EXACTLY ONE Range header, single-range form. See the module header.
        headers: { Range: rangeHeaderValue(options.bytes) },
        // Deprioritised behind the element's own media requests.
        priority: 'low',
        // The media token lives in the URL; no cookie is involved, ever.
        credentials: 'omit',
        signal: controller.signal,
      });

      const bytes = await drain(response);
      if (response.status !== 200 && response.status !== 206) {
        return {
          status: 'failed',
          error: new Error(`prefetch: unexpected status ${String(response.status)}`),
        };
      }
      return { status: 'ok', bytes, httpStatus: response.status };
    } catch (error) {
      if (isAbort(error) || controller.signal.aborted) return { status: 'aborted' };
      return { status: 'failed', error };
    } finally {
      if (external !== undefined) external.removeEventListener('abort', abort);
    }
  };

  return { promise: run(), abort };
}

/** Reads the body to completion so the HTTP cache entry is written; never throws. */
async function drain(response: Response): Promise<number> {
  try {
    const buffer = await response.arrayBuffer();
    return buffer.byteLength;
  } catch {
    return 0;
  }
}
